import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Can } from "../../../components/Can";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useEmpresa } from "../../../contexts/EmpresaContext";
import { useIsMobile } from "../../../hooks/useIsMobile";

import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";
import FiltroBar from "../../../components/FiltroBar/FiltroBar";
import { filtrarPorEmpresa } from "../../../utils/empresaScope";
import CardListMobile from "../../../templates/mobile/CardListMobile";

import { db } from "../../../config/firebase";
import {
  collection,
  getDocs,
  query,
  where,
  deleteDoc,
  doc,
} from "firebase/firestore";

function renderCodigo(p) {
  if (p.codigo) return p.codigo;
  if (p.codigoSequencial != null) {
    return `PE${String(p.codigoSequencial).padStart(4, "0")}`;
  }
  if (p.id) return p.id.slice(0, 8);
  return "-";
}

function PecasPage() {
  const { modoGlobal, empresaAtual } = useEmpresa();
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);

  const [pecas, setPecas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [sidebarAberta, setSidebarAberta] = useState(false);

  const [campoFiltro, setCampoFiltro] = useState("nome");
  const [textoFiltro, setTextoFiltro] = useState("");

  const camposFiltro = [
    { value: "codigo", label: "Código" },
    { value: "nome", label: "Nome" },
    { value: "fornecedorNome", label: "Fornecedor" },
    { value: "maquinaNome", label: "Máquina" },
    { value: "descricao", label: "Descrição" },
    { value: "ondeSeAplica", label: "Onde se Aplica" },
  ];

  useEffect(() => {
    async function carregarPecas() {
      if (!usuarioLogado) return;

      setCarregando(true);
      setErro("");

      try {
        const pecasRef = collection(db, "pecas");
        let q;

        if (usuarioLogado.grupoId) {
          if (!modoGlobal && empresaAtual?.id) {
            q = query(
              pecasRef,
              where("grupoId", "==", usuarioLogado.grupoId),
              where("empresaId", "==", empresaAtual.id)
            );
          } else {
            q = query(
              pecasRef,
              where("grupoId", "==", usuarioLogado.grupoId)
            );
          }
        } else {
          // fallback: não mostra nada se não tiver grupo
          q = query(pecasRef, where("codigo", "==", "__nulo__"));
        }

        const snap = await getDocs(q);
        const lista = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        setPecas(filtrarPorEmpresa(lista, empresaAtual, modoGlobal));
      } catch (e) {
        console.error("Erro ao carregar peças:", e);
        setErro("Erro ao carregar peças. Tente novamente.");
      } finally {
        setCarregando(false);
      }
    }

    carregarPecas();
  }, [usuarioLogado]);

  const pecasFiltradas = pecas.filter((p) => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return true;

    let valorBase = "";

    if (campoFiltro === "codigo") {
      valorBase = renderCodigo(p);
    } else {
      valorBase = p[campoFiltro] ?? "";
    }

    return String(valorBase).toLowerCase().includes(texto);
  });

  async function handleExcluir(pecaId) {
    if (!pecaId) return;

    const confirmar = window.confirm(
      "Tem certeza que deseja excluir esta peça? Essa ação não pode ser desfeita."
    );
    if (!confirmar) return;

    setCarregando(true);
    setErro("");

    try {
      await deleteDoc(doc(db, "pecas", pecaId));
      setPecas((prev) => prev.filter((p) => p.id !== pecaId));
    } catch (e) {
      console.error("Erro ao excluir peça:", e);
      setErro("Erro ao excluir peça. Tente novamente.");
    } finally {
      setCarregando(false);
    }
  }

  const itemsParaCards = pecasFiltradas.map((p) => ({
    id: p.id,
    nome: p.nome || "Sem nome",
    subtitulo: p.fornecedorNome || "-",
    detalhes: [
      { icone: "🔢", label: "Código", valor: renderCodigo(p) },
      { icone: "🏭", label: "Máquina", valor: p.maquinaNome || "-" },
      { icone: "📝", label: "Descrição", valor: p.descricao || "-" },
      {
        icone: "📍",
        label: "Aplica",
        valor: p.ondeSeAplica || "-",
      },
      {
        icone: "📷",
        label: "Fotos",
        valor: Array.isArray(p.fotos) ? `${p.fotos.length}` : p.fotos ? "1" : "0",
      },
    ],
  }));

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Peças"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
        abaBottom="cadastros"
      >
        <CardListMobile
          items={itemsParaCards}
          carregando={carregando}
          erro={erro}
          carregandoMessage="Carregando peças..."
          titulo="Peças"
          searchPlaceholder="Buscar peça..."
          novoLabel="Nova Peça"
          emptyMessage="Nenhuma peça encontrada"
          onHome={() => navigate("/")}
          onNovo={() => navigate("/cadastros/pecas/novo")}
          onEditar={(id) => navigate(`/cadastros/pecas/editar/${id}`)}
          getSearchText={(item) =>
            `${item.nome} ${item.subtitulo}`
          }
        />
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Peças"
      subtitulo=""
    >
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Peças cadastradas</h3>
            <p>
              Lista de peças vinculadas a fornecedores e máquinas do grupo atual.
            </p>
          </div>

          <div className="pc-table-header-actions">
            <Can modulo="cadastros" recurso="pecas" acao="form">
<button
              type="button"
              className="pc-btn-primario"
              onClick={() => navigate("/cadastros/pecas/novo")}
            >
              + Nova Peça
            </button>
</Can>
          </div>
        </header>

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          textoFiltro={textoFiltro}
          onTextoChange={setTextoFiltro}
          onAdicionarClick={() => navigate("/cadastros/pecas/novo")}
          placeholder="Filtrar peças..."
          mostrarAdicionar={false}
        />

        <div className="pc-table-wrapper">
          {erro && (
            <div
              style={{
                background: "#7f1d1d",
                color: "#fecaca",
                padding: 8,
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              {erro}
            </div>
          )}

          {carregando ? (
            <p style={{ padding: 10 }}>Carregando peças...</p>
          ) : pecasFiltradas.length === 0 ? (
            <p style={{ padding: 10 }}>Nenhuma peça encontrada.</p>
          ) : (
            <table className="pc-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nome</th>
                  <th>Fornecedor</th>
                  <th>Máquina</th>
                  <th>Descrição</th>
                  <th>Onde se Aplica</th>
                  <th>Fotos</th>
                  <th>Observações</th>
                  <th style={{ textAlign: "right" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pecasFiltradas.map((p) => (
                  <tr key={p.id}>
                    <td>{renderCodigo(p)}</td>
                    <td>{p.nome || "-"}</td>
                    <td>{p.fornecedorNome || "-"}</td>
                    <td>{p.maquinaNome || "-"}</td>
                    <td>{p.descricao || "-"}</td>
                    <td>{p.ondeSeAplica || "-"}</td>
                    <td>
                      {Array.isArray(p.fotos)
                        ? `${p.fotos.length}`
                        : p.fotos
                        ? "1"
                        : "0"}
                    </td>
                    <td>{p.observacoes || "-"}</td>
                    <td
                      style={{
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Can modulo="cadastros" recurso="pecas" acao="altera">
<button
                        type="button"
                        title="Editar peça"
                        onClick={() =>
                          navigate(`/cadastros/pecas/editar/${p.id}`)
                        }
                        style={{
                          border: "none",
                          
                          cursor: "pointer",
                          fontSize: 18,
                          marginRight: 8,
                        }}
                      >
                        ✏️
                      </button>
</Can>
                      <Can modulo="cadastros" recurso="pecas" acao="altera">
<button
                        type="button"
                        title="Apagar peça"
                        onClick={() => handleExcluir(p.id)}
                        style={{
                          border: "none",
                          
                          cursor: "pointer",
                          fontSize: 18,
                        }}
                      >
                        🗑️
                      </button>
</Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default PecasPage;
